"use client";

import { FeatureHeroSection } from "@/components/shared/feature_hero_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { DynamicEvents } from "@/components/shared/dynamic_events";
import { ContentDownload } from "@/components/shared/content_download";
import { get__related_how_we_help_pages } from "@/cms/how-we-help-data";
import { CustomButton } from "@/components/ui/custom-button";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

const AIWithGuardrails = () => {
    const t = useTranslations();

    return (
        <main className="flex justify-center" role="main" aria-label="StudyStash AI with Guardrails Feature">
            <div className="flex w-full flex-col">
                {/* Hero Section */}
                <FeatureHeroSection
                    icon="/icon-pack/StudyStash - Icon - Lock.png"
                    iconAlt="AI with Guardrails Icon"
                    category="AI with guardrails"
                    title="Secure, responsible AI "
                    highlightedText="with guardrails"
                    description={`StudyStash provides institutional oversight, academic ${t("general.words.rigour")} and ethical parameters that ${t("general.words.universities")} require, distinguishing it from generic AI tools`}
                    buttonText="Our AI-safety Policy"
                    buttonHref="https://trust.studystash.com/resources?s=mu9exjzni8dmja4947aduk&name=jisc:-ai-procurement-due-diligence"
                    showDemoModal={false}
                    secondButtonText="StudyStash AI"
                    secondButtonHref="/ethical-ai/"
                    backgroundImage="/how-we-help/ai-with-guardrails/hero-ai-with-guardrails.png"
                    backgroundImageAlt="StudyStash AI with Guardrails Dashboard"
                    mobileImage="/how-we-help/ai-with-guardrails/hero-ai-with-guardrails.png"
                    mobileImageAlt="StudyStash AI with Guardrails Dashboard"
                />

                {/* Alternating Sections */}
                <div className="pt-24 md:pt-40 lg:pt-72">
                    <AlternatingSection 
                        sections={[
                            {
                                subtitle: "For when generic AI is not enough",
                                title: "Academic excellence through intelligent verification",
                                content: (
                                    <div>
                                        <p>Our AI doesn&apos;t just generate content-it actively protects academic integrity. When students interact with StudyStash, our intelligent verification system cross-references their inputs against course materials to catch potential errors or misconceptions.</p>
                                    </div>
                                ),
                                video: {
                                    src: "/how-we-help/ai-with-guardrails/academic-integrity.gif",
                                    alt: "Demonstration of AI with academic verification",
                                    containerClassName: "rounded-xl bg-ss-purple/20 px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8 object-cover",
                                    type: "gif"
                                }
                            },
                            {
                                subtitle: "Institutional oversight built-in",
                                title: "Grounded learning you can trust",
                                content: (
                                    <div>
                                        <p>Our platform ensures that learning resources are accurate, relevant, and aligned with course objectives. This is because, unlike generic AI tools, StudyStash exclusively uses {t("general.words.lecturers")}&apos; original materials.</p>
                                    </div>
                                ),
                                video: {
                                    src: "/how-we-help/ai-with-guardrails/safe-studystash-ai.gif",
                                    alt: "Demonstration of grounded AI learning",
                                    containerClassName: "rounded-xl bg-ss-green/20 px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8",
                                    type: "gif"
                                }
                            },
                            {
                                subtitle: "More than LTI",
                                title: "Integration with your existing systems",
                                content: (
                                    <div>
                                        <p>If a student is at risk academically, StudyStash ensures universities have the oversight to respond appropriately. By surfacing this intelligence within existing systems, institutions can deliver timely, targeted support to support their students.</p>
                                        <div className="mt-6">
                                            <Link href="/lms-integrations/">
                                                <CustomButton variant="primary" size="md">
                                                    Integration guide
                                                </CustomButton>
                                            </Link>
                                        </div>
                                    </div>
                                ),
                                video: {
                                    src: "/how-we-help/ai-with-guardrails/ai-support-function.gif",
                                    alt: "Demonstration of privacy-focused AI",
                                    containerClassName: "rounded-xl bg-ss-blue/20 px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8",
                                    type: "gif"
                                }
                            },
                            {
                                title: "Contextual learning intelligence",
                                content: (
                                    <div>
                                        <p>StudyStash understands the educational context of student interactions and provides intelligent, course-specific guidance.</p>
                                        <p>For example, if a student types &quot;mitosis&quot; while studying cell division, our AI recognises they might have meant &quot;meiosis&quot;, guiding them toward the correct concept.</p>
                                    </div>
                                ),
                                video: {
                                    src: "/how-we-help/ai-with-guardrails/safe-ai-chat.gif",
                                    alt: "Demonstration of contextual learning assistance",
                                    containerClassName: "rounded-xl bg-ss-yellow/20 px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8 object-cover",
                                    type: "gif"
                                }
                            }
                        ]}
                    />
                </div>

                <div className="relative">
                    {/* Background SVG - Positioned relative to section */}
                    <div className="absolute inset-0 z-0 pointer-events-none">
                        <img 
                            src="/top_line.svg" 
                            alt="Illustration of the StudyStash teaching-learning loop" 
                            className="absolute w-full h-[1000px] object-cover opacity-100"
                            style={{
                                top: '-35%',
                                left: '0%',
                                transform: 'scale(1.0)',
                                objectPosition: 'left center'
                            }}
                        />
                    </div>
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <ContentDownload
                            title="View our Trust and Safety Hub"
                            description="Learn more about how StudyStash ensures academic integrity, data privacy, and responsible AI use in higher education."
                            buttonText="StudyStash AI"
                            buttonHref="/ethical-ai/"
                            showSingleButton={false}
                            secondButtonText="Responsible AI"
                            secondButtonHref="/trustworthy-and-responsible-ai"
                            className="mb-16 md:mb-40"
                        />
                    </div>
                </div>

                {/* Related How We Help Pages */}
                <DynamicEvents 
                    items={get__related_how_we_help_pages("/how-we-help/ai-with-guardrails")}
                    showViewAll={false}
                    showKeepReading={true}
                />
            </div>
        </main>
    );
};

export default AIWithGuardrails;